import { useCallback, useMemo, useState } from 'react';
import type {
  ColumnDef,
  CellContext,
  ColumnPinningState,
  SortingState,
} from '@tanstack/react-table';
import { Button } from '@carbon/react';
import { Clean, Location } from '@carbon/react/icons';
import { useHistory } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  DataTable,
  ExportButton,
  type ExportConfig,
  type TableState,
} from 'components/data-table';
import { LinkCell } from 'components/link-cell';
import { EMPTY_CELL } from 'utils/empty-cell';
import { fetchColumnPinning, updateColumnPinning } from 'utils/tables';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import {
  mapSearchRefAtom,
  SUPPRESS_BOUNDS_SEARCH,
  wellsResultsViewAtom,
} from '../atoms';
import type { DatasetColumnConfig } from '../types';
import type { MapQueryExport } from '../use-map-query-export';
import { CreateWorkspaceMenu } from '../create-workspace-menu';
import { ResultsViewDropdown } from './workspaces-results';
import { MapLocatorButton } from '../subsurface-maps/map-locator-button';
import { ShowOnMapToggle } from './show-on-map-toggle';
import { useTableAreaSelection } from '../create-area/hooks';
import {
  NO_SELECTED_SUBSURFACE_MAP,
  selectedSubsurfaceMapAtom,
} from '../subsurface-maps/atoms';
import {
  useDatasetExport,
  useDatasetSearchChange,
  useReconcile,
  useSelectHighlighted,
} from './hooks';
import { ReconcileWellsTearsheet } from 'pages/well-workspaces/detail/reconcile/reconcile-wells-tearsheet';
import { wellWorkspacePath } from 'pages/well-workspaces/utils';
import { WellsDuplicatesBanner } from './wells-duplicates-banner';
import {
  isReconcileEnabled,
  reconcileFailureText,
  type ReconcileWellsResult,
} from 'pages/well-workspaces/detail/reconcile/hooks';
import type { Bounds, RowData } from './types';
import {
  WELLS_DATASET_ID,
  currentPaging,
  isSubsurfaceMapRow,
  rowExtent,
} from './utils';
import styles from './results-section.module.scss';

const NO_ROWS: RowData[] = [];

// The trailing reconcile ("Action") column's id. One constant for the col def, the
// right-pin, and anything else that keys off it, so they can never drift apart.
const RECONCILE_COLUMN_ID = 'reconcile';

// The localStorage suffix the DataTable persists column pinning under (its STORAGE_SUFFIXES.pinning).
const PINNING_STORAGE_SUFFIX = '-pinning';

/**
 * Force the Action column right-pinned on top of whatever else the user pinned (and never let it sit on
 * the left), merging in the user's own pins. The Action column is enablePinning:false, so the user can't
 * move it themselves.
 * @param {ColumnPinningState} pinning - The user's pinning to merge the forced Action right-pin into.
 * @return {ColumnPinningState} The pinning with the Action column pinned right.
 */
const withReconcilePin = (pinning: ColumnPinningState): ColumnPinningState => ({
  left: (pinning.left ?? []).filter((id) => id !== RECONCILE_COLUMN_ID),
  right: [
    ...(pinning.right ?? []).filter((id) => id !== RECONCILE_COLUMN_ID),
    RECONCILE_COLUMN_ID,
  ],
});

/**
 * A table row's id: its id, else (wells) its UWI.
 * @param {RowData} row The row.
 * @return {string} The id.
 */
const tableRowId = (row: RowData): string => {
  const id = row.id ?? row.uwi;
  return typeof id === 'number' || typeof id === 'string' ? String(id) : '';
};

// Hoisted so the toolbar receives the same array on every render.
const CREATE_WORKSPACE_ACTIONS = [
  <CreateWorkspaceMenu key="create-workspace" />,
];

// Without area selection, the table's options as they have always been.
const TABLE_CONFIG = { columnFilters: false };

// With it: multi-select checkboxes, which only own selection (no row clicks).
const SELECTABLE_TABLE_CONFIG = {
  columnFilters: false,
  selectionType: 'multi' as const,
};

/**
 * The table column for one configured dataset column.
 * @param {DatasetColumnConfig} col The configured column.
 * @return {ColumnDef} The table column.
 */
const buildColumnDef = (
  col: DatasetColumnConfig
): ColumnDef<RowData, unknown> => {
  const width = col.size ?? 150;
  const base = {
    accessorKey: col.accessorKey,
    id: col.id,
    header: col.header,
    size: width,
    minSize: width,
    enableSorting: col.sortable !== false,
  };

  // Column-level meta merged into every branch below: `wrap` lets long text
  // wrap instead of ellipsizing (entity names show in full), and
  // `align` overrides the auto alignment (e.g. left-align numeric columns so
  // their values sit beside the previous column instead of the far edge).
  const wrapMeta = col.wrap ? { wrap: true as const } : undefined;
  const alignMeta = col.align ? { align: col.align } : undefined;
  /**
   * A column's meta merged with its wrap and align settings.
   * @param {Record<string, unknown>} [meta] The branch's own meta.
   * @return {object} The `meta` prop, or nothing when empty.
   */
  const withWrap = (meta?: Record<string, unknown>) => {
    const merged = { ...meta, ...wrapMeta, ...alignMeta };
    return Object.keys(merged).length > 0 ? { meta: merged } : {};
  };

  if (col.cellComponent) {
    const Cell = col.cellComponent;
    return {
      ...base,
      ...withWrap(),
      cell: (info: CellContext<RowData, unknown>) => (
        <Cell value={info.getValue()} />
      ),
    };
  }

  if (col.formatValue) {
    type Info = CellContext<RowData, unknown>;
    return {
      ...base,
      ...withWrap(),
      cell: (info: Info) => col.formatValue!(info.getValue()) ?? '',
    };
  }

  switch (col.type) {
    case 'link':
      return {
        ...base,
        ...withWrap(),
        cell: (info: CellContext<RowData, unknown>) => (
          <LinkCell
            value={info.getValue() as string | null}
            href={
              col.rowHref
                ? col.rowHref(info.row.original)
                : (info.row.original[col.hrefKey ?? ''] as string | null)
            }
            showTitle={!col.wrap}
          />
        ),
      };
    case 'number':
      return {
        ...base,
        ...withWrap({
          type: col.decimals && col.decimals > 0 ? 'decimal' : 'integer',
          format: {
            minDecimalPlaces: col.decimals ?? 0,
            maxDecimalPlaces: col.decimals ?? 0,
          },
        }),
      };
    case 'date':
      return { ...base, ...withWrap({ type: 'date' as const }) };
    case 'datetime':
      return { ...base, ...withWrap({ type: 'datetime' as const }) };
    case 'text':
    case undefined:
      return { ...base, ...withWrap() };
  }
};

/**
 * Selects a row and moves the map to it: fits a subsurface map's footprint
 * extent, else flies to the row's point.
 * @return {Function} Shows a row on the map.
 */
const useShowOnMap = () => {
  const config = useDatasetConfig();
  const mapRef = useAtomValue(mapSearchRefAtom);
  const selectHighlighted = useSelectHighlighted();
  return useCallback(
    (row: RowData) => {
      if (!config.geoLocationKey && !config.extentKey) return;
      const idKey = config.highlightRowKey ?? config.columns[0].accessorKey;
      selectHighlighted(
        String(row[idKey]),
        (row.mapBounds as Bounds | undefined) ?? null
      );
      // An extent (a subsurface map's footprint bounds) fits the whole shape.
      const extent = rowExtent(row, config.extentKey);
      if (mapRef && extent) {
        const [west, south, east, north] = extent;
        mapRef.fitBounds(
          [
            [west, south],
            [east, north],
          ],
          { padding: 32, duration: 800 },
          SUPPRESS_BOUNDS_SEARCH
        );
        return;
      }
      if (!config.geoLocationKey) return;
      const geo = row[config.geoLocationKey] as
        { coordinates: [number, number] } | undefined;
      if (mapRef && geo) {
        const [lng, lat] = geo.coordinates;
        // Tag the move so visible-extent mode doesn't rebound the bounded
        // search and drop the selection we just made.
        mapRef.flyTo(
          { center: [lng, lat], zoom: 14, speed: 1.5 },
          SUPPRESS_BOUNDS_SEARCH
        );
      }
    },
    [mapRef, selectHighlighted, config]
  );
};

/**
 * The configured columns, led by the "show on map" action column when the
 * dataset is mappable. A reconcilable dataset also gets a Potential Duplicates
 * count after Well Name and is trailed by the Reconcile broom.
 * @param {Function} setReconcileAnchor Opens the reconcile tearsheet for a row.
 * @return {ColumnDef[]} The table columns.
 */
const useColumns = (
  setReconcileAnchor: (anchor: { uwi: string; name: string } | null) => void
) => {
  const config = useDatasetConfig();
  const handleShowOnMap = useShowOnMap();
  const selectHighlighted = useSelectHighlighted();
  return useMemo(() => {
    const cols: ColumnDef<RowData, unknown>[] =
      config.columns.map(buildColumnDef);

    if (config.geoLocationKey || config.extentKey) {
      cols.unshift({
        id: 'actions',
        header: '',
        // Icon-only "show on map" button. Per-dataset width (defaults to 70) so
        // a dataset can tighten it to free room for data columns.
        size: config.actionColumnSize ?? 70,
        enableSorting: false,
        meta: { isExportable: false },
        cell: (info: CellContext<RowData, unknown>) => {
          const row = info.row.original;
          if (config.rasterLocator && isSubsurfaceMapRow(row)) {
            return (
              <MapLocatorButton
                row={row}
                onSelect={() => handleShowOnMap(info.row.original)}
                onClear={() => selectHighlighted(null, null)}
              />
            );
          }
          // An extent-only dataset row without an extent has nothing to fit to.
          const nothingToShow =
            !!config.extentKey &&
            !config.geoLocationKey &&
            !rowExtent(info.row.original, config.extentKey);
          if (config.showOnMapToggles) {
            const idKey =
              config.highlightRowKey ?? config.columns[0].accessorKey;
            return (
              <ShowOnMapToggle
                rowId={String(info.row.original[idKey])}
                disabled={nothingToShow}
                onShow={() => handleShowOnMap(info.row.original)}
                onClear={() => selectHighlighted(null, null)}
              />
            );
          }
          return (
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Location}
              iconDescription={
                nothingToShow ? 'No map footprint to show' : 'Show on map'
              }
              hasIconOnly
              disabled={nothingToShow}
              onClick={() => handleShowOnMap(info.row.original)}
            />
          );
        },
      });
    }

    if (config.reconcilable) {
      // The count comes from duplicate_count on each /queries/wells row (the same
      // figure the broom below reads, so they never disagree). The cell shows that
      // live per-request figure; the server-side sort orders by the denormalized ES
      // duplicate_count field (SORT_FIELD_MAP maps potential_duplicates -> it), which
      // matches the shown counts except briefly after a reconcile before the reindex.
      // Not exportable — it is not a stored column on the row.
      const nameIndex = cols.findIndex((col) => col.id === 'name');
      cols.splice(nameIndex === -1 ? cols.length : nameIndex + 1, 0, {
        id: 'potential_duplicates',
        // accessorKey (not just id) is required for the sort control to render:
        // TanStack's getCanSort() gates on `!!column.accessorFn`, so a display
        // column with only an id never shows the sort affordance. The sort itself
        // is server-side (SORT_FIELD_MAP maps this column's id -> duplicate_count).
        accessorKey: 'duplicateCount',
        header: 'Potential Duplicates',
        size: 170,
        enableSorting: true,
        // Surface the wells with the most potential duplicates on the first click.
        sortDescFirst: true,
        meta: { isExportable: false },
        cell: (info: CellContext<RowData, unknown>) => {
          // duplicateCount, not duplicate_count: Jbuilder.key_format camelizes the
          // response, so the backend's json.duplicate_count arrives as duplicateCount.
          const count = info.row.original.duplicateCount as number | undefined;
          return count && count > 0 ? count : EMPTY_CELL;
        },
      });

      cols.push({
        id: RECONCILE_COLUMN_ID,
        header: 'Action',
        size: 90,
        enableSorting: false,
        enableHiding: false,
        // overflowVisible: the broom's tooltip must escape the cell — the column is
        // always on-screen (right-pinned), so a clipping cell would always clip it.
        // enablePinning:false hides this column's own pin toggle (table-header canPin) so the user can't
        // move or un-pin it; it stays right-pinned via the controlled-merge in ResultsTable.
        meta: {
          isExportable: false,
          overflowVisible: true,
          enablePinning: false,
        },
        cell: (info: CellContext<RowData, unknown>) => {
          const row = info.row.original;
          const uwi = row.uwi as string | undefined;
          const count = row.duplicateCount as number | undefined;
          // Only a row the detector actually paired with something can be
          // reconciled; offering the action on every row would promise a sheet
          // with nothing in it.
          if (!uwi || !(count && count > 0)) return null;
          return (
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Clean}
              iconDescription="Reconcile potential duplicates"
              tooltipPosition="left"
              hasIconOnly
              onClick={(event) => {
                // The row itself is clickable; this opens the sheet, not the row.
                event.stopPropagation();
                setReconcileAnchor({
                  uwi,
                  name: (row.name as string | undefined) ?? uwi,
                });
              }}
            />
          );
        },
      });
    }

    return cols;
  }, [config, handleShowOnMap, selectHighlighted, setReconcileAnchor]);
};

/**
 * Subsurface Maps: the rows that read as selected (v3) — the one whose raster is
 * on the map, and the footprint selected (clicked) on the map.
 * @return {Set<string> | undefined} The selected row ids.
 */
const useRasterRowIds = () => {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const highlightId = useAtomValue(atoms.highlightedItemIdAtom);
  // Other datasets read a constant atom, so showing a raster never re-renders them.
  const shownRaster = useAtomValue(
    config.rasterLocator
      ? selectedSubsurfaceMapAtom
      : NO_SELECTED_SUBSURFACE_MAP
  );
  return useMemo(() => {
    if (!config.rasterLocator || (!shownRaster && !highlightId))
      return undefined;
    const ids = new Set<string>();
    if (shownRaster) ids.add(String(shownRaster.id));
    if (highlightId) ids.add(String(Number(highlightId)));
    return ids;
  }, [config.rasterLocator, shownRaster, highlightId]);
};

/**
 * The flat table layout: server-driven paging, sorting and search, with the
 * show-on-map column and CSV export.
 * @param {object} props The table.
 * @param {MapQueryExport} props.mapQueryExport The page's CSV export.
 * @return {JSX.Element} The table.
 */
export function ResultsTable({
  mapQueryExport,
}: {
  mapQueryExport: MapQueryExport;
}) {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const persistenceKey = `${config.id}-search-results`;
  const { data, loading, currentPayload } = useAtomValue(atoms.searchAtom);
  const setSearch = useSetAtom(atoms.searchAtom);
  const datasetText = useAtomValue(atoms.datasetTextAtom);
  const handleSearchChange = useDatasetSearchChange();
  const {
    visibleRows,
    reconcileAnchor,
    setReconcileAnchor,
    reconcilePool,
    reconcileGeometry,
    pastVersions,
    reconcileLoading,
    reconcileLoadError,
    reconcileWells,
  } = useReconcile((data?.results ?? NO_ROWS) as RowData[]);
  const columns = useColumns(setReconcileAnchor);
  const rasterRowIds = useRasterRowIds();
  // Reconcile is enabled by default; an environment with WELL_DEDUPE_RECONCILE_ENABLED=false surfaces as
  // this flag (JsConfig). When off, the sheet stays explorable but its submit is disabled.
  const reconcileEnabled = isReconcileEnabled();

  // The Wells table keeps its Action column right-pinned while letting the user pin other columns: the
  // DataTable would apply its stored pinning wholesale and drop the Action pin, so pinning is controlled
  // here and re-merged to keep the Action column right (never left). The Action column is
  // enablePinning:false, so the user can't move it themselves. Seeded from the user's SAVED pinning — the
  // same localStorage the DataTable persists under (persistenceKey + "-pinning") — merged with the Action
  // pin, and every change is written back, so a user's pins survive navigating away and back instead of
  // resetting to Action-only each mount.
  const pinningStorageKey = `${persistenceKey}${PINNING_STORAGE_SUFFIX}`;
  const [reconcilePinning, setReconcilePinning] = useState<ColumnPinningState>(
    () => withReconcilePin(fetchColumnPinning(pinningStorageKey))
  );
  const handleReconcilePinningChange = useCallback(
    (next: ColumnPinningState) => {
      const merged = withReconcilePin(next);
      setReconcilePinning(merged);
      updateColumnPinning(pinningStorageKey, merged);
    },
    [pinningStorageKey]
  );

  // A reconcile lands the operator on the single workspace it promoted through (see onReconcile), so there
  // is no success toast to hold — the redirect is the confirmation.
  const history = useHistory();
  // Checkboxes whose rows seed Create New Area; null for a dataset that can't
  // seed one, leaving the table unchanged.
  const areaSelection = useTableAreaSelection(
    config,
    data?.results ?? NO_ROWS,
    tableRowId
  );
  const { exportDataset, disabledReason } = useDatasetExport(mapQueryExport);
  const exportConfig: ExportConfig = {
    exportPresets: [
      {
        name: 'Download',
        format: 'csv',
        // The button sends the visible, exportable columns in on-screen order,
        // and stays disabled (showing the reason) while an export can't start.
        columns: 'visible',
        disabledReason,
        onExport: (_format, _filters, _name, cols) => exportDataset(cols),
      },
    ],
  };

  const handleSortChange = useCallback(
    (newSorting: SortingState) => {
      const sort = newSorting.at(0);
      setSearch((current) => ({
        ...(current ?? config.defaultPayload),
        sortAttribute: sort?.id,
        sortDirection: sort ? (sort.desc ? 'DESC' : 'ASC') : undefined,
        page: 1,
      }));
    },
    [setSearch, config.defaultPayload]
  );

  // Server-driven pagination.
  const handlePaginationChange = useCallback(
    (state: TableState) => {
      setSearch((current) => ({
        ...(current ?? config.defaultPayload),
        page: state.page,
        pageSize: state.pageSize,
      }));
    },
    [setSearch, config.defaultPayload]
  );

  const [resultsView, setResultsView] = useAtom(wellsResultsViewAtom);
  const isWellsDataset = config.id === WELLS_DATASET_ID;

  return (
    <div className={styles.container}>
      {config.reconcilable && (
        <WellsDuplicatesBanner
          totalDuplicateCount={data?.totalDuplicateCount ?? 0}
        />
      )}
      <div className={styles.tableArea}>
        <DataTable
          data={visibleRows}
          columns={columns}
          getRowId={tableRowId}
          highlightedRowIds={rasterRowIds}
          isLoading={!!loading}
          persistenceKey={persistenceKey}
          leftToolbarActions={[
            ...(isWellsDataset
              ? [
                  <ResultsViewDropdown
                    key="wells-results-view"
                    value={resultsView}
                    onChange={setResultsView}
                  />,
                ]
              : []),
            ...(config.canCreateDcaWorkspace ? CREATE_WORKSPACE_ACTIONS : []),
          ]}
          // Pagination is uncontrolled — the table seeds its page index once from
          // initialTableState on mount. This section unmounts when its tab is
          // inactive (see page.tsx), so on remount we seed from the atom's
          // currentPayload (the last-searched page), not defaultPayload's page 1.
          // Otherwise the paginator would read "1" while the cached rows are still
          // page N.
          initialTableState={currentPaging(
            currentPayload,
            config.defaultPayload
          )}
          // Server-driven: pagination, sorting, and text search are all handled
          // by /queries/wells. This disables the client-side global filter, which
          // otherwise re-filters the current page literally -- dropping every row
          // when the search term contains quotes.
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount: data?.totalCount ?? 0,
          }}
          // A reconcilable dataset (Wells) keeps its Action column pinned right through horizontal
          // scroll AND lets the user pin other columns (restored after the Action-only regression).
          // Pinning stays enabled; it is CONTROLLED here so the user's pinning can be re-merged with the
          // forced Action right-pin on every change (onColumnPinningChange below) — a controlled value is
          // the only way to guarantee the Action pin, since the DataTable restores stored pinning
          // wholesale (hooks.ts getItem) and would otherwise drop it. The Action column is
          // enablePinning:false so the user can't move it; other columns' toggles work via the merge.
          config={areaSelection ? SELECTABLE_TABLE_CONFIG : TABLE_CONFIG}
          // Search is controlled by the per-dataset applied text (same atom the
          // cards-layout Search box reads) so it survives this section's remount —
          // a dataset-tab switch, or (for Wells) toggling the View dropdown to
          // Workspaces and back — instead of resetting blank every time.
          controlledState={{
            globalFilter: datasetText,
            ...(config.reconcilable ? { columnPinning: reconcilePinning } : {}),
            ...(areaSelection ? { selection: areaSelection.selection } : {}),
          }}
          onColumnPinningChange={
            config.reconcilable ? handleReconcilePinningChange : undefined
          }
          onSelectionChange={areaSelection?.onSelectionChange}
          onPaginationChange={handlePaginationChange}
          onSortChange={(s) => handleSortChange(s.sorting)}
          onGlobalSearchChange={(s) => handleSearchChange(s.globalFilter)}
          searchPlaceholder={
            config.searchPlaceholder ?? `Search ${config.label}`
          }
          rightToolbarActions={[
            <ExportButton key="export" config={exportConfig} />,
          ]}
        />
      </div>
      {reconcileAnchor ? (
        <ReconcileWellsTearsheet
          open
          anchorName={reconcileAnchor.name}
          anchorUwi={reconcileAnchor.uwi}
          pool={reconcilePool}
          geometry={reconcileGeometry}
          pastVersions={pastVersions}
          loading={reconcileLoading}
          loadError={reconcileLoadError}
          committing={reconcileWells.isPending}
          commitError={reconcileFailureText(reconcileWells.error)}
          reconcileEnabled={reconcileEnabled}
          onClose={() => {
            // Safe: close is blocked while committing, so onSuccess survives and prior error is cleared.
            reconcileWells.reset();
            setReconcileAnchor(null);
          }}
          onReconcile={(committed) =>
            reconcileWells.mutate(committed, {
              onSuccess: (result: ReconcileWellsResult) => {
                setReconcileAnchor(null);
                // Every group (single or multi-survivor) promotes through ONE shared workspace, so land the
                // operator on it rather than showing a link toast (UAT: redirect to the workspace; all
                // survivors share one). groups[0] carries that shared id — identical across groups.
                const workspaceId = result.groups[0]?.workspaceId ?? null;
                if (workspaceId !== null) {
                  history.push(wellWorkspacePath(workspaceId));
                  return;
                }
                // Fallback only — a successful reconcile always has a workspace id, so this branch should
                // not run. If it ever does, stay on the table and refetch: the reconciled duplicates leave
                // the survivor, so its live duplicate_count drops (and may reach 0, retiring its broom), and
                // the per-request listing count then refreshes every row and drops the retired duplicates.
                setSearch((current) => ({
                  ...(current ?? config.defaultPayload),
                }));
              },
            })
          }
        />
      ) : null}
    </div>
  );
}
